/**
 * Responsive facility notes table modeled after the clinical-trials prototype.
 *
 * Rows preserve note unread state independently from notification document
 * badges and delegate exact deep-link navigation to the data-owning page.
 */

import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import type { ClinicalNoteResponse } from "../../api/clinicalTrialsApiTypes";

interface NotesTableProps {
  notes: ClinicalNoteResponse[];
  isLoading: boolean;
  error: string | null;
  onOpen: (note: ClinicalNoteResponse) => void;
  onRetry: () => void;
}

/** Format an API timestamp for compact notes-table display. */
function formatDate(value?: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

/**
 * Render loading, failure, empty, and populated notes inbox states.
 *
 * @param props - Note rows and interaction callbacks
 * @returns Facility notes table
 */
export function NotesTable({
  notes,
  isLoading,
  error,
  onOpen,
  onRetry,
}: NotesTableProps) {
  if (isLoading) {
    return <div className="rounded-lg border bg-white py-16 text-center text-sm">Loading notes…</div>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-statusRed-text/30 bg-white py-14 text-center">
        <p className="mb-3 text-sm text-statusRed-text">{error}</p>
        <button type="button" onClick={onRetry} className="btn btn-outline px-4 py-2 text-sm">Retry</button>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-uiGray-200 bg-white">
      <table className="w-full min-w-[980px] text-sm">
        <thead className="border-b border-uiGray-200 bg-uiGray-150">
          <tr>
            <th className="w-8 px-3 py-3" aria-label="Unread" />
            {["Note", "Patient / MRN", "Sender", "Last Activity", "Assignee", "Due Date", "Status"].map(
              (column) => <th key={column} className="px-4 py-3 text-left text-xs font-semibold">{column}</th>,
            )}
          </tr>
        </thead>
        <tbody>
          {!notes.length ? (
            <tr><td colSpan={8} className="px-4 py-16 text-center text-primaryGray-8">No notes match your filters.</td></tr>
          ) : notes.map((note) => (
            <tr
              key={note.note_id}
              onClick={() => onOpen(note)}
              className={`cursor-pointer border-b border-uiGray-200 transition-colors last:border-0 hover:bg-uiGray-50 ${
                note.is_unread ? "bg-blue-50/50" : ""
              }`}
            >
              <td className="px-3 py-3 text-center">
                {note.is_unread && <span className="inline-block h-2 w-2 rounded-full bg-statusBlue-text" />}
              </td>
              <td className={`max-w-[260px] px-4 py-3 text-xs font-semibold ${note.is_unread ? "text-statusBlue-darkText" : ""}`}>
                {note.title}
              </td>
              <td className="px-4 py-3 text-xs font-medium">
                {note.patient_name || "Unknown patient"} <span className="font-normal text-primaryGray-8">({note.mrn})</span>
              </td>
              <td className="px-4 py-3 text-xs">{note.creator.display_name || note.creator.email || "Unknown"}</td>
              <td className="whitespace-nowrap px-4 py-3 text-xs text-primaryGray-8">{formatDate(note.updated_at)}</td>
              <td className="px-4 py-3 text-xs">{note.assignee?.display_name || note.assignee?.email || "Unassigned"}</td>
              <td className={`whitespace-nowrap px-4 py-3 text-xs ${note.is_overdue ? "font-semibold text-statusRed-text" : ""}`}>
                {!note.due_date
                  ? "No Due Date Assigned"
                  : note.is_overdue
                    ? `Overdue · ${formatDate(note.due_date)}`
                    : formatDate(note.due_date)}
              </td>
              <td className="px-4 py-3">
                <Tags
                  label={
                    note.status === "resolved"
                      ? "Resolved"
                      : note.is_overdue
                        ? "Overdue"
                        : "Open"
                  }
                  backgroundColor={
                    note.status === "resolved" ? "#dcfce7" : undefined
                  }
                  textColor={
                    note.status === "resolved" ? "#166534" : undefined
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
